import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "Your brand, your workspace — company name, logo, and fee structure",
  "Full CRM, dispute pipeline, certified-mail tracking & billing included",
  "Unlimited team members with role-based access",
  "Your clients get their own branded portal",
];

export default function AgencySignup() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const [form, setForm] = useState({ company_name: "", owner_name: "", email: "", password: "", ref: searchParams.get("ref") || "", invite: inviteToken, plan: "credit_solo", agree_policy: false });
  const [busy, setBusy] = useState(false);
  const [plansData, setPlansData] = useState({ plans: [], refund_policy: "" });
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/public/plans").then((r) => setPlansData(r.data)).catch(() => {});
    if (inviteToken) {
      api.get(`/public/invite/${inviteToken}`).then((r) => {
        const inv = r.data;
        if (inv.status !== "pending") {
          setInviteError(inv.status === "joined"
            ? "This invite link was already used. Sign in instead, or ask your D&Z representative for a new link."
            : "This invite link has expired. Ask your D&Z representative for a fresh one — links are valid for 7 days.");
          return;
        }
        setInvite(inv);
        setForm((f) => ({ ...f, owner_name: inv.name, email: inv.email, plan: inv.plan }));
      }).catch(() => setInviteError("This invite link is invalid. Ask your D&Z representative for a fresh one."));
    }
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_policy) return toast.error("Please accept the refund policy to continue");
    setBusy(true);
    try {
      await api.post("/auth/agency-signup", form);
      await login(form.email, form.password);
      toast.success("Your white-label workspace is ready");
      navigate("/admin");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <Building2 className="h-9 w-9 text-[#0B2545]" />
            <div className="font-heading font-extrabold text-2xl text-[#0B2545] tracking-tight">White-Label Partner</div>
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">Launch your credit repair platform</h1>
          <p className="text-sm text-slate-500 mb-6">Get your own branded copy of the platform — your logo, your pricing, your clients. Ready in 60 seconds.</p>
          {inviteError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 mb-4" data-testid="invite-error-banner">{inviteError}</div>
          )}
          {invite && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 mb-4" data-testid="invite-welcome-banner">
              Welcome, <b>{invite.name}</b> — your invite from D&Z Management is applied. Your plan is locked in below.
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" data-testid="agency-signup-form">
            <div><Label>Company / brand name</Label><Input required value={form.company_name} onChange={set("company_name")} placeholder="BrightScore Credit Repair" data-testid="agency-company-input" className="mt-1" /></div>
            <div><Label>Your name</Label><Input required value={form.owner_name} onChange={set("owner_name")} placeholder="Renee Solis" data-testid="agency-owner-input" className="mt-1" /></div>
            <div><Label>Work email</Label><Input type="email" required value={form.email} onChange={set("email")} placeholder="you@youragency.com" data-testid="agency-email-input" className="mt-1" /></div>
            <div><Label>Password (min 8 characters)</Label><Input type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="agency-password-input" className="mt-1" /></div>
            <div className="space-y-2">
              <Label>{invite ? "Your plan (set by D&Z Management)" : "Choose your plan"}</Label>
              {invite ? (
                <div className="rounded-lg border-2 border-[#0B2545] bg-blue-50 p-3" data-testid="plan-locked">
                  <div className="text-sm font-semibold text-slate-800">{invite.plan_name}</div>
                  <div className="text-lg font-bold font-heading text-[#0B2545] mt-1">
                    ${invite.plan_setup}{invite.plan_monthly > 0
                      ? <span className="text-xs font-normal text-slate-500"> + ${invite.plan_monthly}/month</span>
                      : <span className="text-xs font-normal text-slate-500"> one-time · no monthly fee</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Includes the full CRM + DIY letter portal for your clients, under your brand.</div>
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="plan-select">
                {plansData.plans.map((p) => (
                  <button type="button" key={p.id} onClick={() => setForm({ ...form, plan: p.id })} data-testid={`plan-${p.id}`}
                    className={`rounded-lg border-2 p-3 text-left transition-colors duration-150 ${form.plan === p.id ? "border-[#0B2545] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="text-sm font-semibold text-slate-800">{p.name}</div>
                    <div className="text-lg font-bold font-heading text-[#0B2545] mt-1">
                      ${p.setup}{p.monthly > 0
                        ? <span className="text-xs font-normal text-slate-500"> + ${p.monthly}/month</span>
                        : <span className="text-xs font-normal text-slate-500"> one-time · no monthly fee</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.desc}</div>
                  </button>
                ))}
              </div>
              )}
            </div>
            <label className="flex items-start gap-2.5 text-xs text-slate-600 rounded-md bg-slate-50 border border-slate-200 p-3">
              <Checkbox checked={form.agree_policy} onCheckedChange={(v) => setForm({ ...form, agree_policy: !!v })} className="mt-0.5" data-testid="policy-consent-checkbox" />
              <span><b>Refund policy:</b> {plansData.refund_policy || "All sales are final on DIY plans. White-label licenses are non-refundable once your first client is submitted."}</span>
            </label>
            <Button type="submit" disabled={busy} data-testid="agency-signup-submit-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">
              {busy ? "Building your workspace…" : "Create my white-label workspace"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-4">
            Already have an account? <Link to="/login" className="text-blue-700 font-medium hover:underline" data-testid="agency-go-login-link">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex bg-[#0B2545] text-white items-center">
        <div className="p-12 max-w-lg">
          <h2 className="font-heading text-4xl font-extrabold leading-tight mb-6">Sell the platform.<br />Keep your brand.</h2>
          <div className="space-y-4">
            {PERKS.map((p) => (
              <div key={p} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-sky-300 mt-0.5 shrink-0" />
                <p className="text-sky-100 text-sm leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
